import type { Permissions, UsePermissionsType } from '@app/src/auth/permissions'
import { usePermissions } from '@app/src/auth/permissions'
import {
  buttonResetSx,
  labelFadeTransition,
  NAV_ITEM_HEIGHT,
  NAV_ITEM_PX,
  navLabelSx,
} from '@app/src/components/Navigation/VerticalMenu/navItemStyles'
import { useVerticalMenu } from '@app/src/components/Navigation/VerticalMenu/VerticalMenuContext'
import { MENU_ITEM_HEIGHT } from '@app/src/theme/components/MuiMenuItem'
import { toSnakeCase } from '@app/src/utils/helpers'
import { ChevronRight } from '@mui/icons-material'
import type { Theme } from '@mui/material'
import { Box, Menu, MenuItem, Typography } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'
import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

const TRANSITION = 'color 0.2s ease-in-out, background-color 0.2s ease-in-out'

type MenuLinkItem = {
  link: string
  permission?: Permissions | Permissions[]
}

type MenuItems = {
  title: string
  link: string
  permission?: Permissions | Permissions[]
}

export type MenuLinkWrapperProps = {
  title: string
  icon?: React.ReactNode
  menuItems: MenuLinkItem
  activePaths?: string[]
  external?: boolean
  /** Renders the item in the promoted style, whatever the current route. */
  promoted?: boolean
}

export type MenuWrapperProps = {
  title: string
  icon?: React.ReactNode
  menuItems: MenuItems[]
  activePaths?: string[]
}

const isActive = (menuItems: MenuLinkItem | MenuItems[], pathname: string, activePaths?: string[]): boolean => {
  if (activePaths?.some(path => pathname.startsWith(path))) return true
  return [menuItems].flat().some(item => pathname.startsWith(item.link))
}

const hasItemPermission = (
  item: MenuLinkItem | MenuItems,
  hasPermission: UsePermissionsType['hasPermission'],
): boolean => {
  const perms = [item.permission].flat().filter(Boolean) as Permissions[]
  return perms.length === 0 || perms.some(p => hasPermission(p))
}

const hasPermissionToShow = (
  menuItems: MenuLinkItem | MenuItems[],
  hasPermission: UsePermissionsType['hasPermission'],
) => {
  const guardedItems = [menuItems].flat().filter(item => item.permission)
  return guardedItems.length === 0 || guardedItems.some(item => hasItemPermission(item, hasPermission))
}

/** A promoted item keeps its accent whether or not it is the current page, so it reads as an offer. */
const promotedMenuItemSx: SystemStyleObject<Theme> = {
  color: 'secondary.main',
  backgroundColor: 'success.light',
  '&:hover': { color: 'secondary.main', backgroundColor: 'success.main' },
}

const menuItemSx = (active: boolean, highlighted: boolean): SystemStyleObject<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  minHeight: NAV_ITEM_HEIGHT,
  px: NAV_ITEM_PX,
  borderRadius: 2,
  cursor: 'pointer',
  textDecoration: 'none',
  color: active || highlighted ? 'text.primary' : 'text.secondary',
  backgroundColor: active || highlighted ? 'grey.200' : 'transparent',
  transition: TRANSITION,
  '& > .MuiSvgIcon-root': { flexShrink: 0 },
  '&:hover': {
    color: 'text.primary',
    backgroundColor: 'action.hover',
  },
})

export const MenuLinkWrapper = ({
  title,
  menuItems,
  activePaths,
  external,
  icon,
  promoted = false,
}: MenuLinkWrapperProps): JSX.Element | null => {
  const { hasPermission } = usePermissions()
  const { pathname } = useLocation()
  const { isExpanded } = useVerticalMenu()

  const menuItem = [menuItems].flat()[0]
  if (!hasPermissionToShow(menuItem, hasPermission)) return null

  const active = isActive(menuItem, pathname, activePaths)

  return (
    <Box
      component={Link}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      to={menuItem.link}
      sx={[menuItemSx(active, false), promoted && promotedMenuItemSx]}
    >
      {icon}
      <Typography variant="subtitle2" color="inherit" noWrap sx={[{ flexGrow: 1 }, navLabelSx(isExpanded)]}>
        {title}
      </Typography>
    </Box>
  )
}

const CLOSE_DELAY_MS = 200

const MENU_LIST_PADDING_Y = 8
const FIRST_MENU_ITEM_CENTER = MENU_LIST_PADDING_Y + MENU_ITEM_HEIGHT / 2

const MenuWrapper = ({ title, menuItems, activePaths, icon }: MenuWrapperProps): JSX.Element | null => {
  const [isHovered, setIsHovered] = useState(false)
  const { hasPermission } = usePermissions()
  const { pathname } = useLocation()
  const anchorRef = useRef<HTMLAnchorElement>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { isExpanded, isStableExpanded, onPopoverOpen, onPopoverClose, openFlyoutId, openFlyout, closeFlyout } =
    useVerticalMenu()

  const menuId = `menu-${toSnakeCase(title)}`
  const open = openFlyoutId === menuId

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    onPopoverOpen()
    return onPopoverClose
  }, [open, onPopoverOpen, onPopoverClose])

  useEffect(() => {
    if (!isStableExpanded && open) closeFlyout(menuId)
  }, [isStableExpanded, open, closeFlyout, menuId])

  useEffect(() => {
    if (isHovered && isStableExpanded && !open) openFlyout(menuId)
  }, [isHovered, isStableExpanded, open, openFlyout, menuId])

  if (!hasPermissionToShow(menuItems, hasPermission)) return null

  const active = isActive(menuItems, pathname, activePaths)
  const highlighted = isHovered || open
  const firstItemLink = menuItems.find(item => hasItemPermission(item, hasPermission))?.link ?? pathname

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const scheduleClose = () => {
    cancelClose()
    closeTimerRef.current = setTimeout(() => closeFlyout(menuId), CLOSE_DELAY_MS)
  }

  const handleAnchorMouseEnter = () => {
    setIsHovered(true)
    cancelClose()
    if (isStableExpanded) openFlyout(menuId)
  }

  const handleAnchorMouseLeave = () => {
    setIsHovered(false)
    scheduleClose()
  }

  const handleAnchorKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight') return
    e.preventDefault()
    cancelClose()
    openFlyout(menuId)
  }

  const handleMenuClose = () => {
    cancelClose()
    setIsHovered(false)
    closeFlyout(menuId)
  }

  const handleItemClick = () => {
    handleMenuClose()
  }

  return (
    <>
      <Box
        component={Link}
        to={firstItemLink}
        data-cy="menu-btn"
        ref={anchorRef}
        onKeyDown={handleAnchorKeyDown}
        onMouseEnter={handleAnchorMouseEnter}
        onMouseLeave={handleAnchorMouseLeave}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        sx={[buttonResetSx, menuItemSx(active, highlighted)]}
      >
        {icon}
        <Typography variant="subtitle2" color="inherit" noWrap sx={[{ flexGrow: 1 }, navLabelSx(isExpanded)]}>
          {title}
        </Typography>
        <ChevronRight
          sx={[
            navLabelSx(isExpanded),
            {
              color: highlighted ? 'text.secondary' : 'transparent',
              fontSize: '20px',
              flexShrink: 0,
              transition: `${TRANSITION}, ${labelFadeTransition(isExpanded)}`,
              'a:hover &, a:focus-visible &': { color: 'text.secondary' },
            },
          ]}
        />
      </Box>

      <Menu
        id={menuId}
        open={open}
        anchorEl={anchorRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'center' }}
        transformOrigin={{ horizontal: 'left', vertical: FIRST_MENU_ITEM_CENTER }}
        elevation={1}
        onClose={handleMenuClose}
        sx={{ pointerEvents: 'none' }}
        MenuListProps={{
          onMouseEnter: cancelClose,
          onMouseLeave: scheduleClose,
          sx: { pointerEvents: 'auto' },
        }}
        slotProps={{ paper: { sx: { pointerEvents: 'auto', minWidth: 300 } } }}
      >
        {menuItems.map(item => (
          <MenuItem
            key={item.link}
            to={item.link}
            component={Link}
            onClick={handleItemClick}
            data-testid={`menu-item-${toSnakeCase(item.title)}`}
            sx={theme => ({
              borderLeft: item.link === pathname ? `3px solid ${theme.palette.primary.main}` : 'none',
            })}
          >
            <Box display="flex" alignItems="center" py={1}>
              <Typography variant="subtitle2" color="text.primary">
                {item.title}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default MenuWrapper
